import axios from 'axios';

interface Comment {
  id: number;
  content: string;
  authorName: string;
  authorEmail: string;
  authorWebsite?: string;
  contentType: 'blog' | 'case_study';
  contentId: number;
  parentId?: number;
  status: 'pending' | 'approved' | 'rejected' | 'spam';
  isAuthorReply: boolean;
  likeCount: number;
  createdAt: string;
  updatedAt: string;
  replies?: Comment[];
  parent?: Comment;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  etag?: string;
  lastModified?: string;
}

interface CacheStorage {
  [key: string]: CacheEntry<any>;
}

interface CommentFormData {
  content: string;
  authorName: string;
  authorEmail: string;
  authorWebsite?: string;
  parentId?: number;
}

interface CommentResponse {
  success: boolean;
  data?: {
    comments: Comment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
  message?: string;
}

interface CommentStatsResponse {
  success: boolean;
  data?: {
    totalComments: number;
    pendingComments: number;
  };
  message?: string;
}

class CommentService {
  private baseURL = '/api/comments';
  private cache: CacheStorage = {};
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for comments
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of cached comment sets

  private getCacheKey(endpoint: string, params?: string): string {
    return `comments:${endpoint}${params ? `?${params}` : ''}`;
  }

  private isCacheValid(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp < this.CACHE_DURATION;
  }

  private cleanupCache(): void {
    const keys = Object.keys(this.cache);
    if (keys.length > this.MAX_CACHE_SIZE) {
      // Remove oldest entries
      const sortedKeys = keys.sort((a, b) => 
        this.cache[a].timestamp - this.cache[b].timestamp
      );
      const keysToRemove = sortedKeys.slice(0, keys.length - this.MAX_CACHE_SIZE);
      keysToRemove.forEach(key => delete this.cache[key]);
    }
  }

  private async makeCachedRequest<T>(
    endpoint: string, 
    params?: string, 
    useCache: boolean = true
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedEntry = this.cache[cacheKey];

    // If we have valid cached data and caching is enabled, return it
    if (useCache && cachedEntry && this.isCacheValid(cachedEntry)) {
      return cachedEntry.data;
    }

    try {
      // Prepare headers for conditional requests
      const headers: Record<string, string> = {};
      if (cachedEntry?.etag) {
        headers['If-None-Match'] = cachedEntry.etag;
      }
      if (cachedEntry?.lastModified) {
        headers['If-Modified-Since'] = cachedEntry.lastModified;
      }

      const response = await axios.get(`${this.baseURL}${endpoint}${params ? `?${params}` : ''}`, {
        headers
      });

      // If content hasn't changed (304 Not Modified), return cached data
      if (response.status === 304 && cachedEntry) {
        // Update timestamp to extend cache validity
        cachedEntry.timestamp = Date.now();
        return cachedEntry.data;
      }

      // Store new data in cache
      const newCacheEntry: CacheEntry<T> = {
        data: response.data,
        timestamp: Date.now(),
        etag: response.headers.etag,
        lastModified: response.headers['last-modified']
      };

      this.cache[cacheKey] = newCacheEntry;
      this.cleanupCache();

      return response.data;
    } catch (error: any) {
      // If request fails and we have cached data, return it as fallback
      if (cachedEntry) {
        console.warn('API request failed, using cached data:', error.message);
        return cachedEntry.data;
      }
      throw error;
    }
  }

  /**
   * Get comments for a specific content (blog or case study)
   */
  async getComments(
    contentType: 'blog' | 'case_study',
    contentId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<CommentResponse> {
    try {
      const params = `page=${page}&limit=${limit}&status=approved`;
      const response = await this.makeCachedRequest<CommentResponse>(
        `/${contentType}/${contentId}`,
        params
      );
      return response;
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch comments');
    }
  }

  /**
   * Create a new comment
   */
  async createComment(
    contentType: 'blog' | 'case_study',
    contentId: number,
    commentData: CommentFormData
  ): Promise<{ success: boolean; data?: Comment; message?: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/${contentType}/${contentId}`, commentData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating comment:', error);
      throw new Error(error.response?.data?.message || 'Failed to create comment');
    }
  }

  /**
   * Like a comment
   */
  async likeComment(commentId: number): Promise<{ success: boolean; data?: { likeCount: number }; message?: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/${commentId}/like`);
      return response.data;
    } catch (error: any) {
      console.error('Error liking comment:', error);
      throw new Error(error.response?.data?.message || 'Failed to like comment');
    }
  }

  /**
   * Get comment statistics for a content
   */
  async getCommentStats(
    contentType: 'blog' | 'case_study',
    contentId: number
  ): Promise<CommentStatsResponse> {
    try {
      const response = await this.makeCachedRequest<CommentStatsResponse>(
        `/${contentType}/${contentId}/stats`
      );
      return response;
    } catch (error: any) {
      console.error('Error fetching comment stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch comment statistics');
    }
  }

  /**
   * Update comment status (for moderation)
   */
  async updateCommentStatus(
    commentId: number,
    status: 'pending' | 'approved' | 'rejected' | 'spam'
  ): Promise<{ success: boolean; data?: Comment; message?: string }> {
    try {
      const response = await axios.patch(`${this.baseURL}/${commentId}/status`, { status });
      return response.data;
    } catch (error: any) {
      console.error('Error updating comment status:', error);
      throw new Error(error.response?.data?.message || 'Failed to update comment status');
    }
  }

  /**
   * Delete a comment
   */
  async deleteComment(commentId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axios.delete(`${this.baseURL}/${commentId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete comment');
    }
  }

  /**
   * Format relative time (e.g., "2 hours ago")
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate URL format
   */
  isValidURL(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Clean and format comment content
   */
  formatCommentContent(content: string): string {
    return content
      .trim()
      .replace(/\n\s*\n/g, '\n\n') // Remove extra blank lines
      .substring(0, 2000); // Ensure max length
  }

  /**
   * Cache Management Methods
   */

  /**
   * Invalidate cache for specific content
   */
  invalidateCache(contentType: 'blog' | 'case_study', contentId: number): void {
    const keys = Object.keys(this.cache);
    const pattern = `comments:/${contentType}/${contentId}`;
    
    keys.forEach(key => {
      if (key.includes(pattern)) {
        delete this.cache[key];
      }
    });
  }

  /**
   * Invalidate all comment cache
   */
  invalidateAllCache(): void {
    this.cache = {};
  }

  /**
   * Refresh specific content comments (bypass cache)
   */
  async refreshComments(
    contentType: 'blog' | 'case_study',
    contentId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<CommentResponse> {
    try {
      const params = `page=${page}&limit=${limit}&status=approved`;
      const response = await this.makeCachedRequest<CommentResponse>(
        `/${contentType}/${contentId}`,
        params,
        false // Don't use cache
      );
      return response;
    } catch (error: any) {
      console.error('Error refreshing comments:', error);
      throw new Error(error.response?.data?.message || 'Failed to refresh comments');
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[]; oldestEntry?: number; newestEntry?: number } {
    const keys = Object.keys(this.cache);
    const timestamps = keys.map(key => this.cache[key].timestamp);
    
    return {
      size: keys.length,
      keys,
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : undefined,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : undefined
    };
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredCache(): number {
    const keys = Object.keys(this.cache);
    let clearedCount = 0;
    
    keys.forEach(key => {
      if (!this.isCacheValid(this.cache[key])) {
        delete this.cache[key];
        clearedCount++;
      }
    });
    
    return clearedCount;
  }
}

export default new CommentService();
export type { Comment, CommentFormData, CommentResponse, CommentStatsResponse };
